import React, { useEffect, useState, useRef, lazy } from 'react';
import { ExternalLink, BookOpen, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
interface BooksSectionProps {
  variant?: 'full' | 'compact';
}
const AUTHOR_URL = 'https://www.amazon.com/stores/author/B0G7LGV9RL/allbooks?_encoding=UTF8&ref_=aufs_ap_ahdr_dsk_ab';
const books = [{
  title: 'Medical First Aid at Sea',
  subtitle: 'Practical Handbook for Seafarers and Offshore Personnel',
  asin: 'B0GF8YHWNX',
  coverUrl: 'https://m.media-amazon.com/images/P/B0GF8YHWNX.01._SCLZZZZZZZ_SX500_.jpg',
  gradient: 'from-teal-600 via-emerald-800 to-slate-900',
  amazonUrl: 'https://www.amazon.com/Medical-First-Aid-Sea-Practical-ebook/dp/B0GF8YHWNX'
}, {
  title: 'Fire Safety & Firefighting Manual',
  subtitle: 'Practical Fire Survival Guide for Seafarers & Offshore Crews',
  asin: 'B0GHP5QPQP',
  coverUrl: 'https://m.media-amazon.com/images/P/B0GHP5QPQP.01._SCLZZZZZZZ_SX500_.jpg',
  gradient: 'from-cyan-700 via-teal-800 to-slate-900',
  amazonUrl: 'https://www.amazon.com/Fire-Safety-Firefighting-Manual-Konovalenko-ebook/dp/B0GHP5QPQP'
}, {
  title: 'Documents You Need for Offshore',
  subtitle: '2026 Edition: STCW, DP Certification, Flag & Offshore Compliance Guide',
  asin: 'B0GQQCRTB5',
  coverUrl: 'https://m.media-amazon.com/images/P/B0GQQCRTB5.01._SCLZZZZZZZ_SX500_.jpg',
  gradient: 'from-slate-800 via-gray-900 to-black',
  amazonUrl: 'https://www.amazon.com/Documents-Need-Offshore-2026-Certification-ebook/dp/B0GQQCRTB5'
}, {
  title: 'H2S Survival Guide',
  subtitle: 'Real Offshore Experience, Practical Drills and Safety Checklists',
  asin: 'B0G7Y8LGT4',
  coverUrl: 'https://m.media-amazon.com/images/P/B0G7Y8LGT4.01._SCLZZZZZZZ_SX500_.jpg',
  gradient: 'from-slate-700 via-gray-800 to-slate-950',
  amazonUrl: 'https://www.amazon.com/H2S-Survival-Guide-Offshore-Personnel-ebook/dp/B0G7Y8LGT4'
}, {
  title: 'Permit to Work Explained',
  subtitle: 'Practical Offshore Safety Guide for PTW, Toolbox Talks and Stop Work Authority',
  asin: 'B0GBJ2T219',
  coverUrl: 'https://m.media-amazon.com/images/P/B0GBJ2T219.01._SCLZZZZZZZ_SX500_.jpg',
  gradient: 'from-teal-700 via-cyan-800 to-slate-900',
  amazonUrl: 'https://www.amazon.com/Permit-Work-Explained-Practical-Authority-ebook/dp/B0GBJ2T219'
}, {
  title: 'Marlins Test Lifehacks',
  subtitle: 'Insider Tips & Proven Strategies from Experienced Seafarers',
  asin: 'B0G67BX73G',
  coverUrl: 'https://m.media-amazon.com/images/P/B0G67BX73G.01._SCLZZZZZZZ_SX500_.jpg',
  gradient: 'from-purple-700 via-fuchsia-800 to-slate-900',
  amazonUrl: 'https://www.amazon.com/MARLINS-TEST-LIFEHACS-Strategies-Experienced-ebook/dp/B0G67BX73G'
}, {
  title: 'Marlins Test Lifehacks',
  subtitle: 'Astuces internes et stratégies éprouvées pour les marins expérimentés (French Edition)',
  asin: 'B0G7M27TPT',
  coverUrl: 'https://m.media-amazon.com/images/P/B0G7M27TPT.01._SCLZZZZZZZ_SX500_.jpg',
  gradient: 'from-purple-600 via-indigo-800 to-slate-900',
  amazonUrl: 'https://www.amazon.com/MARLINS-TEST-LIFEHACKS-strat%C3%A9gies-exp%C3%A9riment%C3%A9s-ebook/dp/B0G7M27TPT'
}];
export function BooksSection({
  variant = 'full'
}: BooksSectionProps) {
  const {
    language
  } = useLanguage();
  const isKa = language === 'ka';
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const content = {
    badge: isKa ? 'ჩვენი დამფუძნებლის ავტორობით' : 'Published by Our Founder',
    title: isKa ? 'ოფშორული გადარჩენისა და კარიერის სახელმძღვანელოები' : 'Offshore Survival & Career Guides',
    desc: isKa ? 'პრაქტიკული სახელმძღვანელოები, დაწერილი ოლეჰ კონოვალენკოს მიერ, რეალურ ოფშორულ გამოცდილებაზე დაყრდნობით. ხელმისაწვდომია Amazon-ზე.' : 'Practical handbooks written by Oleh Konovalenko, based on real offshore experience. Available on Amazon.',
    viewAmazon: isKa ? 'ნახეთ Amazon-ზე' : 'View on Amazon',
    viewAll: isKa ? 'ყველა წიგნის ნახვა' : 'View All Books',
    author: isKa ? 'ოლეჰ კონოვალენკო' : 'Oleh Konovalenko'
  };
  const handleImageError = (asin: string) => {
    setFailedImages(prev => new Set(prev).add(asin));
  };
  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll, {
      passive: true
    });
    window.addEventListener('resize', checkScroll);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, []);
  const scroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector('a')?.offsetWidth || 280;
    const gap = 24;
    const scrollAmount = dir === 'left' ? -(cardWidth + gap) : cardWidth + gap;
    el.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });
  };
  return <section className={`w-full px-6 md:px-12 ${variant === 'full' ? 'py-16 md:py-20' : 'py-12'} bg-[var(--color-bg)]`} data-id="element-22">
      <div className="max-w-7xl mx-auto" data-id="element-23">
        {/* Header */}
        <div className={`flex flex-col ${variant === 'compact' ? 'md:flex-row md:items-end justify-between' : 'text-center'} mb-10`} data-id="element-24">
          <div className={variant === 'full' ? 'max-w-2xl mx-auto' : 'max-w-xl'} data-id="element-25">
            <div className={`inline-flex items-center gap-2 px-4 py-1.5 border border-gray-300 rounded-full mb-6 bg-white`} data-id="element-26">
              <BookOpen className="w-4 h-4 text-[var(--color-tan)]" data-id="element-27" />
              <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-28">
                {content.badge}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-text-dark)] mb-4" data-id="element-29">
              {content.title}
            </h2>
            {variant === 'full' && <p className="text-base text-[var(--color-text-light)]" data-id="element-30">
                {content.desc}
              </p>}
          </div>

          {variant === 'compact' && <a href={AUTHOR_URL} target="_blank" rel="noopener noreferrer" className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--color-tan)] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity mt-4 md:mt-0" data-id="element-31">
              {content.viewAll}
              <ExternalLink className="w-4 h-4" data-id="element-32" />
            </a>}
        </div>

        {/* Scrollable books container */}
        <div className="relative group/scroll" data-id="element-33">
          {/* Left arrow — always visible when scrollable */}
          <button onClick={() => scroll('left')} className={`hidden md:flex absolute -left-5 top-[35%] -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white border border-gray-200 shadow-md items-center justify-center hover:bg-gray-50 hover:shadow-lg active:scale-95 transition-all ${canScrollLeft ? 'opacity-100 cursor-pointer' : 'opacity-0 pointer-events-none'}`} aria-label="Scroll left" data-id="element-34">
            <ChevronLeft className="w-5 h-5 text-[var(--color-text-dark)]" data-id="element-35" />
          </button>

          {/* Right arrow — always visible when scrollable */}
          <button onClick={() => scroll('right')} className={`hidden md:flex absolute -right-5 top-[35%] -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white border border-gray-200 shadow-md items-center justify-center hover:bg-gray-50 hover:shadow-lg active:scale-95 transition-all ${canScrollRight ? 'opacity-100 cursor-pointer' : 'opacity-0 pointer-events-none'}`} aria-label="Scroll right" data-id="element-36">
            <ChevronRight className="w-5 h-5 text-[var(--color-text-dark)]" data-id="element-37" />
          </button>

          {/* Scroll container */}
          <div ref={scrollRef} className="flex gap-4 md:gap-6 overflow-x-auto scroll-smooth pb-4 -mx-1 px-1 snap-x snap-mandatory" style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch'
        }} data-id="element-38">
            <style data-id="element-39">{`
              .books-scroll::-webkit-scrollbar { display: none; }
            `}</style>
            {books.map((book, idx) => <a key={idx} href={book.amazonUrl} target="_blank" rel="noopener noreferrer" className="group flex-shrink-0 flex flex-col snap-start" style={{
            width: 'calc(50vw - 2.25rem)',
            maxWidth: '280px'
          }} data-id="element-40">
                {/* Book Cover — natural image size, no crop */}
                <div className="w-full mb-3 md:mb-4 rounded-lg md:rounded-xl overflow-hidden shadow-[2px_4px_12px_rgba(0,0,0,0.12)] md:shadow-[4px_6px_16px_rgba(0,0,0,0.15)] transition-all duration-300 group-hover:-translate-y-1.5 group-hover:shadow-[6px_10px_24px_rgba(0,0,0,0.2)]" data-id="element-41">
                  {book.coverUrl && !failedImages.has(book.asin) ? <img src={book.coverUrl} alt={book.title} className="w-full h-auto block" loading="lazy" onError={() => handleImageError(book.asin)} data-id="element-42" /> : <div className={`relative w-full aspect-[2/3] bg-gradient-to-br ${book.gradient}`} data-id="element-43">
                      {/* Spine effect */}
                      <div className="absolute left-0 top-0 bottom-0 w-2.5 bg-gradient-to-r from-black/25 to-transparent" data-id="element-44" />
                      {/* Cover content */}
                      <div className="absolute inset-0 p-5 flex flex-col" data-id="element-45">
                        <div className="flex-1 flex flex-col justify-center" data-id="element-46">
                          <h3 className="text-white font-bold text-lg leading-tight mb-2 drop-shadow-sm" data-id="element-47">
                            {book.title}
                          </h3>
                          <p className="text-white/70 text-xs leading-relaxed line-clamp-3" data-id="element-48">
                            {book.subtitle}
                          </p>
                        </div>
                        <div className="pt-3 border-t border-white/15" data-id="element-49">
                          <p className="text-white/80 text-[10px] font-medium tracking-widest uppercase" data-id="element-50">
                            {content.author}
                          </p>
                        </div>
                      </div>
                    </div>}
                </div>

                {/* Book info below cover */}
                <h4 className="text-xs md:text-sm font-bold text-[var(--color-text-dark)] mb-0.5 md:mb-1 group-hover:text-[var(--color-tan)] transition-colors line-clamp-2" data-id="element-51">
                  {book.title}
                </h4>
                <p className="text-[10px] md:text-xs text-[var(--color-text-light)] mb-2 md:mb-3 line-clamp-2 flex-1" data-id="element-52">
                  {book.subtitle}
                </p>
                <span className="inline-flex items-center gap-1 text-[10px] md:text-xs font-medium text-[var(--color-tan)]" data-id="element-53">
                  {content.viewAmazon}
                  <ExternalLink className="w-3 h-3 md:w-3.5 md:h-3.5" data-id="element-54" />
                </span>
              </a>)}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className={`mt-8 ${variant === 'full' ? 'text-center' : 'text-center md:hidden'}`} data-id="element-55">
          <a href={AUTHOR_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--color-tan)] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity" data-id="element-56">
            {content.viewAll}
            <ExternalLink className="w-4 h-4" data-id="element-57" />
          </a>
        </div>
      </div>
    </section>;
}